import React, { useState, useEffect, useRef } from 'react';
import { ogafAction } from "@ogaf/react.scripts/Utils/ogafAction";

/**
 * Komponenta pro výběr jazyka.
 */
interface LanguageData {
    /** Seznam dostupných jazyků. */
    LanguageEntries: LanguageEntry[];
}

/**
 * Reprezentuje jednotlivý jazyk.
 */
interface LanguageEntry {
    /** Název jazyka, který se zobrazuje uživateli. */
    Caption: string;
    /** Odkaz na jazykovou verzi aplikace. */
    Link: string;
    /** Ikona jazyka, název klíče ikonky z db */
    Icon: string;
    /** Pořadí jazyka v seznamu */
    Order: number;
    /** Indikuje, zda je tento jazyk aktuálně vybraný. */
    Selected: boolean;
}

/**
 * Props komponenty LanguageSelector.
 */
interface LanguageProps {
    /**
     * Id komponenty, defaultně "languageSelectorComponent".
     */
    id?: string;
    /**
     * Ručně předaná data pro inicializaci komponenty, pokud nejsou poskytnuta, komponenta načte data z backendu.
     */
    initialData?: LanguageData;
    /**
     * Vlastní akce při výběru jazyka.
     */
    onLanguageSelect?: (link: string) => void;
    /**
     * Funkce pro získání dat s dostupnými jazyky.
     * @returns Promise s daty dostupných jazyků.
     */
    getFetchData?: () => Promise<LanguageData>;
    /**
     * Jméno zprávy pro získání dat dostupných jazyků, defaultně "msg_getLanguagesEntries".
     */
    getCustomMessageType?: string;
    /**      
     * Data dostupných jazyků.
     * */
    getMsgData?: Record<string, string>;
    /**
     * Funkce pro uložení dat dostupných jazyků.
     * @param link - odkaz na jazykovou verzi aplikace (např. cs-CZ, sk-SK, apod. ).
     * @returns Promise s aktualizovanými daty dostupných jazyků.
     */
    setFetchData?: (link: string) => Promise<LanguageData>;
    /**
     * Jméno zprávy pro uložení dat dostupných jazyků, defaultně "msg_setLanguage".
     */
    setCustomMessageType?: string;
    /**
     * Data pro uložení dostupných jazyků.
     */
    setMsgData?: Record<string, string>;
}

const LanguageSelector: React.FC<LanguageProps> = ({
    id = "languageSelectorComponent",
    initialData,
    onLanguageSelect,
    getFetchData,
    getCustomMessageType = "msg_getLanguagesEntries",
    getMsgData,
    setFetchData,
    setCustomMessageType = "msg_setLanguage",
    setMsgData
}) => {
    const [isShowPopup, setIsShowPopup] = useState(false);
    const [data, setData] = useState<LanguageData | null>(initialData ?? null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    /**
     * Načte data při prvním renderu komponenty, pokud nejsou poskytnuta initialData. 
     */
    useEffect(() => {
        if (!initialData) {
            loadData();
        }
    }, []);

    /**
     * Kontroluje kliknutí mimo komponentu a zavře popup, pokud je otevřený.
     */
    useEffect(() => {
        if (!isShowPopup) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            if (contentRef.current && !contentRef.current.contains(target)) {
                setIsShowPopup(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isShowPopup]);


    /**
     * Načte data dostupných jazyků z backendu.
     */
    const loadData = async () => {
        try {
            let data: Record<string, string> = {};
            if (getMsgData) {
                data = getMsgData;
            }

            const result: LanguageData = getFetchData
                ? await getFetchData()
                : await ogafAction<LanguageData>(getCustomMessageType, data, "/LanguageSelector/DigestMessage");
            setData(result);
        } catch (error) {
            console.error('Error fetching data for LanguageSelector:', error);
        }
    };

    /**
     * Vybere jazyk a aktualizuje data komponenty.
     */
    const chooseLanguage = async (link: string) => {
        try {
            let result: LanguageData;
            if (setFetchData) {
                result = await setFetchData(link);
            } else {
                const data: Record<string, string> = { ...setMsgData, link };
                result = await ogafAction<LanguageData>(setCustomMessageType, data, "/LanguageSelector/DigestMessage");
            }
            setData(result);
        } catch (error) {
            console.error('Error saving data for LanguageSelector:', error);
        }
    };

    /**
     * Pomocná funkce pro zpracování kliknutí na jazyk. 
     * Pokud je poskytnuta funkce onLanguageSelect, zavolá ji s odkazem na jazyk. 
     * Jinak zavolá chooseLanguage pro aktualizaci jazyka.
     */
    const handleClick = async (link: string) => {
        if (onLanguageSelect) {
            onLanguageSelect(link);
            return;
        }

        await chooseLanguage(link);
    };

    /**
     * Renderuje obsah popupu s dostupnými jazyky. Pokud nejsou dostupné žádné jazyky, zobrazí zprávu "Žádné dostupné jazyky.".
     */
    const renderContent = () => {
        return (
            <div
                ref={contentRef}
                className={`languages-content${isShowPopup ? '' : ' d-none'}`}
            >
                {data?.LanguageEntries
                    ?.slice()
                    .sort((a, b) => a.Order - b.Order)
                    .map((entry) => (
                        <div className="item d-flex align-items-center gap-2" key={`item-${entry.Order}`} onClick={() => handleClick(entry.Link)}>
                            <img src={`ImageView.ashx?img=${entry.Icon}&amp;format=svg`} width="18" height="18" alt="language-icon"/>
                            <div>{entry.Caption}</div>
                        </div>
                    ))}
                {data?.LanguageEntries && data?.LanguageEntries.length <= 0 &&
                    <div className="item nodata text-center">Žádné dostupné jazyky.</div>
                }
            </div>
        )
    };

    /**
     * Renderuje hlavní komponentu LanguageSelector, která zobrazuje aktuálně vybraný jazyk a při kliknutí zobrazí popup s dostupnými jazyky.
     */
    return (
        <div
            className="languages"
            id={id}
            onClick={() => setIsShowPopup(true)}
        >
            <img id="imgFlag" src={`ImageView.ashx?img=${data?.LanguageEntries?.find(entry => entry.Selected)?.Icon}&amp;format=svg`} width="18" height="18" alt="language-icon"/>
            {renderContent()}
        </div>
    );
};

export default LanguageSelector;